import { useMemo } from 'react';
/* eslint-disable no-nested-ternary */
import { AppstoreOutlined, BellOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { paths } from '@app/constants';
import { RolesSwitcher } from '@app/pages/shared/RolesSwitcher';
import { useLogout } from '@app/utils';
import { useUnreadNotificationsCount, useUserTrialsOverview } from '@data/hooks';
import { useOptimisticSignUpStore } from '@data/storage';
import {
  AntdBadge,
  AntdGrid,
  AntdTypography,
  AppLogo,
  Button,
  COLOR_BASE_800,
  ChatIcon,
  SignOut,
  isBHS,
  isCurewiki,
  useTheme,
} from '@ui';
import { AccountSwitcher, LanguageSwitcher, MobileNavBar } from './components';

import './nav-bar.css';

const { Text } = AntdTypography;
const { useBreakpoint } = AntdGrid;

export interface NavBarProps {
  hideLinks?: boolean;
}

export const NavBar = ({ hideLinks = false }: NavBarProps) => {
  const { lg } = useBreakpoint();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { logout } = useLogout();
  const { textColor } = useTheme();
  const { isLoading } = useOptimisticSignUpStore();

  const { data: overview } = useUserTrialsOverview(!isLoading);
  const { data: unreadNotifications } = useUnreadNotificationsCount(!isLoading);

  const defaultNavBarLinks = useMemo(
    () => [
      { link: `/${paths.app}/${paths.chat}`, name: t('common.chat'), icon: ChatIcon },
      {
        link: `/${paths.app}/${paths.notifications}`,
        name: t('common.notifications'),
        icon: BellOutlined,
        count: unreadNotifications?.count ?? 0,
      },
      {
        link: `/${paths.app}/${paths.trials}`,
        name: t('common.my_trials'),
        icon: AppstoreOutlined,
        count: overview?.eligible ?? 0,
      },
    ],
    [language, unreadNotifications, overview?.eligible],
  );

  if (!lg)
    return (
      <div className="flex items-center justify-between w-full">
        <NavLink to={`/${paths.app}`}>
          <AppLogo />
        </NavLink>
        <div className="flex items-center gap-1 ml-1">
          <LanguageSwitcher />
          <AccountSwitcher />
          <RolesSwitcher />
          {hideLinks ? null : (
            <AntdBadge dot={defaultNavBarLinks.some((link) => !!link.count)} offset={[0, 5]}>
              <MobileNavBar links={defaultNavBarLinks} />
            </AntdBadge>
          )}
        </div>
      </div>
    );
  return (
    <>
      <div className="flex items-center gap-4">
        {!isLoading ? (
          <NavLink to={`/${paths.app}`}>
            <AppLogo />
          </NavLink>
        ) : (
          <AppLogo />
        )}
        <RolesSwitcher />
      </div>
      {/* The switcher stays outside the hideLinks gate, as on mobile — a patient mid-sign-up
          still needs a way out of the wrong language. */}
      <div className="flex gap-4 items-center h-full" data-testid="header-links">
        {hideLinks
          ? null
          : defaultNavBarLinks.map((link) => {
              const LinkIcon = link.icon;
              return (
                <NavLink
                  key={link.name}
                  to={link.link}
                  className={`active-link ${
                    isCurewiki && 'curewiki-active-link'
                  } outline-[#9ab0d9] focus-visible:outline-4 focus-visible:outline focus-visible:outline-offset-1`}
                  style={({ isActive }) => ({
                    color: isActive ? textColor : isBHS ? COLOR_BASE_800 : textColor,
                    borderBottom: isActive ? `2px solid ${textColor}` : 'none',
                    marginBottom: isActive ? '-2px' : '0',
                  })}
                >
                  {link.icon && <LinkIcon />}
                  <AntdBadge
                    overflowCount={5}
                    count={link.count}
                    styles={{ indicator: { borderRadius: 4, opacity: 0.9 } }}
                    className="text-inherit"
                    size="small"
                  >
                    <Text className="ml-1" style={{ color: 'inherit' }}>
                      {link.name}
                    </Text>
                  </AntdBadge>
                </NavLink>
              );
            })}

        <LanguageSwitcher />

        {hideLinks ? null : (
          <>
            <AccountSwitcher />

            {/* common links */}
            <Button
              type="link"
              target="_blank"
              rel="noopener noreferrer"
              href="mailto:patients@curewiki.health"
              icon={<QuestionCircleOutlined />}
              className="px-0"
            >
              {t('support.text')}
            </Button>
            <Button
              danger
              onClick={() => logout()}
              className="flex items-center justify-center px-0 bg-transparent"
              type="text"
            >
              <SignOut className="text-error" />
              <Text className="ml-1 text-error block">{t('common.logout')}</Text>
            </Button>
          </>
        )}
      </div>
    </>
  );
};
